import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { INFOS_PAGE, htmlKey, pageOfInstance } from "../pages/repeatable";
import { useSitePages } from "../pages/nav";
import { titleKey, freeId } from "../pages/prose";
import { useMode } from "../panel/mode";
import { useSettings, useT } from "../settings/context";
import { Icon } from "../ui/Icon";
import { scrollingPane } from "../ui/scroll";
import { sanitize } from "../ui/Prose";

/**
 * ═══ UNE SECTION : LE HTML DE L'ORGANISATEUR, HABILLÉ PAR LE PRODUIT ═══
 *
 * « Juste une page où l'on met du HTML », et on peut en ajouter autant qu'on veut
 * sur l'accueil : cet écran les sert TOUTES. La première répond à `/infos` avec les
 * clés nues ; chaque exemplaire ajouté répond à `/infos/<n>` avec les clés suffixées
 * (`pages/repeatable.ts`), exactement comme l'étape répétable du parcours.
 *
 * ⚠️ LE CONTENU EST À LUI, LE CHÂSSIS EST À NOUS. La V1 rend un `<div>` NU
 * (`screens/sections/index.jsx`) : ce que l'organisateur colle EST la page, pas de
 * titre, pas de marge, et un tableau écrit dans un back-office de 1 400 px déborde
 * d'un téléphone. Le prototype a corrigé, pas copié ; on porte sa correction :
 *
 *   1. le SOMMAIRE est DÉRIVÉ des `h3` du contenu (aucune saisie de plus), et ne
 *      s'affiche qu'à partir de deux entrées : un plan d'un seul point n'oriente rien ;
 *   2. les TABLEAUX sont emballés dans un cadre qui défile (`.se-tablewrap`) ;
 *   3. le REPLI DE LANGUE est DIT au participant : la cascade sert la langue de
 *     référence en silence, et l'anglophone qui reçoit du français croit à un bug.
 *
 * Displayed strings are French, except the fallback notice, deliberately: telling
 * an English reader IN FRENCH that the page is not in English defeats itself. It is
 * the one translated string of the site, exactly as in the prototype.
 */

/**
 * L'avertissement de repli, dans la langue du LECTEUR. Les langues y sont nommées
 * par leur code court, celui que le sélecteur d'en-tête affiche déjà : c'est le nom
 * sous lequel le participant connaît sa langue ici, et il n'a pas besoin d'être
 * traduit. Une langue de lecture hors de ces quatre retombe sur le français, la
 * langue du produit.
 */
const FALLBACK: Record<
  string,
  (read: string, served: string) => [string, string]
> = {
  fr: (read, served) => [
    `Cette page n'existe pas encore en ${read}.`,
    ` Vous lisez la version en ${served}, la seule dont dispose l'organisateur.`,
  ],
  en: (read, served) => [
    `This page is not available in ${read} yet.`,
    ` You are reading the ${served} version, the only one the organiser has published.`,
  ],
  es: (read, served) => [
    `Esta página aún no existe en ${read}.`,
    ` Está leyendo la versión en ${served}, la única de la que dispone el organizador.`,
  ],
  de: (read, served) => [
    `Diese Seite gibt es noch nicht auf ${read}.`,
    ` Sie lesen die Fassung auf ${served}, die einzige, die der Veranstalter bereitstellt.`,
  ],
};

/** Une entrée du sommaire : l'ancre posée dans la prose, et le titre qu'elle porte. */
type Entry = { id: string; title: string };

export function Infos() {
  const t = useT();
  /* `/infos` → pas d'instance, clés nues ; `/infos/2` → clés suffixées `_2`. */
  const instance = useParams().instance;

  const { detail, scope, languages } = useSettings();
  const { name, subtitle } = useSitePages();
  const organiser = useMode() === "organiser";

  const page = pageOfInstance(INFOS_PAGE, instance);
  const title = name(page);
  const sub = subtitle(page);

  /**
   * Le contenu, et D'OÙ il vient : c'est le niveau de la résolution qui permet de
   * dire le repli au lecteur : ce que `pickLocalized()` de la V1 jette, précisément.
   */
  const d = detail(htmlKey(INFOS_PAGE, instance));
  const html = typeof d.value === "string" ? d.value : "";
  const collapsed =
    scope.languageId !== scope.defaultLanguageId &&
    (d.level === "default" || d.level === "typo-default") &&
    html.trim() !== "";

  const short = (id: number) =>
    languages.find((l) => l.id === id)?.short ?? String(id);
  const read = short(scope.languageId);
  const alert = (FALLBACK[read.toLowerCase()] ?? FALLBACK["fr"])!(
    read,
    short(scope.defaultLanguageId),
  );

  /**
   * ═══ L'ÉQUIPEMENT DE LA PROSE ═══
   *
   * Le HTML est posé tel quel (`dangerouslySetInnerHTML`, la même confiance que le
   * produit accorde déjà à l'organisateur sur son bloc d'accueil), puis le produit
   * l'équipe : tableaux emballés, ancres posées sur les `h3`, plan relevé.
   *
   * ⚠️ REJOUABLE, PAS SEULEMENT REJOUÉ, c'est la leçon du prototype : son équipement
   * tournait une fois au démarrage, et changer de contenu cumulait les sommaires et
   * laissait les tableaux de la page précédente emballés. Ici l'effet se rejoue quand
   * le HTML change (React repose alors le contenu à neuf) et chaque geste est
   * idempotent : un tableau déjà emballé est laissé tel quel, les ancres se
   * recalculent des mêmes titres.
   */
  const prose = useRef<HTMLElement | null>(null);
  const [plan, setPlan] = useState<ReadonlyArray<Entry>>([]);

  useEffect(() => {
    const corps = prose.current;
    if (!corps) {
      setPlan([]);
      return;
    }

    for (const table of Array.from(corps.querySelectorAll("table"))) {
      const parent = table.parentElement;
      if (!parent || parent.classList.contains("se-tablewrap")) continue;
      const frame = document.createElement("div");
      frame.className = "se-tablewrap";
      parent.insertBefore(frame, table);
      frame.appendChild(table);
    }

    /* Les `h3` de PREMIER niveau seulement, comme le prototype : un titre enfoui dans
       une colonne du contenu n'est pas un jalon de lecture. */
    const taken = new Set<string>();
    const entries: Entry[] = [];
    for (const child of Array.from(corps.children)) {
      if (child.tagName !== "H3") continue;
      const id = freeId(titleKey(child.textContent), taken);
      taken.add(id);
      child.id = id;
      entries.push({ id, title: child.textContent ?? "" });
    }
    setPlan(entries);
  }, [html]);

  /**
   * LA POSITION DE LECTURE. La ligne de lecture est au premier TIERS du cadre, pas à
   * son bord, sinon le titre suivant remplit l'écran pendant que le sommaire annonce
   * encore le précédent. En bas de course, c'est la dernière entrée : on y est.
   *
   * ⚠️ LE SITE DÉFILE DANS `.scroll`, PAS DANS LA FENÊTRE : c'est le piège mesuré de
   * l'album : tout ce qui écoute la fenêtre reste muet ici. On remonte au vrai cadre.
   */
  const [luId, setLuId] = useState<string | null>(null);

  useEffect(() => {
    const corps = prose.current;
    if (!corps || plan.length < 2) return;
    const frame = scrollingPane(corps);
    if (!frame) return;

    let expected = false;
    const reading = () => {
      expected = false;
      const top = frame.getBoundingClientRect().top;
      const line = top + frame.clientHeight * 0.3;
      let current = plan[0]!.id;
      for (const { id } of plan) {
        const anchor = document.getElementById(id);
        if (anchor && anchor.getBoundingClientRect().top <= line) current = id;
      }
      if (frame.scrollTop + frame.clientHeight >= frame.scrollHeight - 4) {
        current = plan[plan.length - 1]!.id;
      }
      setLuId(current);
    };
    const surDefilement = () => {
      if (expected) return;
      expected = true;
      requestAnimationFrame(reading);
    };

    reading();
    frame.addEventListener("scroll", surDefilement, { passive: true });
    return () => frame.removeEventListener("scroll", surDefilement);
  }, [plan]);

  const toAnchor = (id: string) => (e: React.MouseEvent) => {
    /* Un vrai lien (atteignable au clavier), mais le saut est le nôtre : laisser
       faire le navigateur ferait défiler la PAGE, pas le cadre. */
    e.preventDefault();
    const anchor = document.getElementById(id);
    if (!anchor) return;
    anchor.scrollIntoView({ behavior: "smooth", block: "start" });
    anchor.setAttribute("tabindex", "-1");
    (anchor as HTMLElement).focus({ preventScroll: true });
  };

  const withToc = plan.length >= 2;

  /**
   * ⚠️ L'OBJET `{__html}` EST MÉMOÏSÉ, ET C'EST CE QUI GARDE L'ÉQUIPEMENT EN VIE.
   * Mesuré ici : recréé à chaque rendu, React repose l'innerHTML À CHAQUE COMMIT,
   * chaîne pourtant identique, et chaque re-rendu du site (une requête qui aboutit,
   * un survol du panneau) redéballait les tableaux et arrachait les ancres posées
   * par l'effet, qui n'écoute que le HTML et ne rejouait donc rien. Les autres
   * écrans passent l'objet inline sans dégât : ils ne touchent jamais au DOM de
   * leur bloc, la remise à neuf y est invisible. Celui-ci l'équipe ; il mémoïse.
   */
  const content = useMemo(() => ({ __html: sanitize(html) }), [html]);

  return (
    /* `data-page` : l'exemplaire, qui borne une règle d'apparence à CETTE section —
       voir `selectorOf`. */
    <section className="page" data-page={page.key}>
      <div className="ttl" data-cfg="infosTitle">
        <h2>{title}</h2>
        {sub.trim() !== "" && <p>{sub}</p>}
      </div>

      {/* Le repli de langue, DIT, dans la langue du lecteur, marquée sur le bloc. */}
      {collapsed && (
        <p className="se-alt" role="status" lang={read.toLowerCase()}>
          <Icon name="monde" />
          <span>
            <b>{alert[0]}</b>
            {alert[1]}
          </span>
        </p>
      )}

      {html.trim() === "" ? (
        /* ⚠️ DIT À L'ORGANISATEUR, seul à pouvoir le corriger : le même geste que la
           satisfaction sans formulaire : une page publiée vide n'est pas un écran
           blanc, c'est une configuration à finir. ⚠️ À LUI SEUL : le participant n'a
           ni panneau où écrire ni menu d'où retirer la page. Il lit un état vide
           neutre, plutôt qu'un titre seul qu'on prendrait pour un chargement en plan. */
        organiser ? (
          <div className="alert" data-cfg="infosBody">
            <span className="alert__d" aria-hidden="true">
              <i />
            </span>
            <span>
              <b>Cette page n'a pas encore de contenu</b>
              <small>
                Écrivez-le dans le panneau (c'est votre HTML, servi tel quel), ou
                retirez la page du menu.
              </small>
            </span>
          </div>
        ) : (
          <p className="empty" data-cfg="infosBody">
            {t("PAGE_CONTENT_SOON", "Le contenu de cette page arrive bientôt.")}
          </p>
        )
      ) : (
        /* ⚠️ L'ARTICLE EST APPARIÉ PAR CLÉ, PAS PAR POSITION. Mesuré sans les clés :
           le sommaire apparaît en PREMIER enfant, la réconciliation positionnelle
           donnait sa place à la nav et REMONTAIT un article neuf ; React y reposait
           le HTML brut (même chaîne, nouveau nœud), tables déballées, ancres
           perdues, et l'effet, qui n'écoute que le HTML, ne rejouait rien. Les
           puces du sommaire, elles, existaient : l'état `plan` avait bien été
           relevé sur le nœud défunt. */
        <div className={withToc ? "se-layout se-layout--toc" : "se-layout"}>
          {withToc && (
            <nav
              key="toc"
              className="se-toc"
              aria-label={t("PAGE_CONTENTS", "Sommaire de la page")}
            >
              <b className="se-toc__t">{t("CONTENTS", 'Sommaire')}</b>
              <div className="se-toc__l">
                {plan.map((entry) => (
                  <a
                    key={entry.id}
                    className="se-toc__i"
                    href={`#${entry.id}`}
                    aria-current={entry.id === luId ? "location" : undefined}
                    onClick={toAnchor(entry.id)}
                  >
                    {entry.title}
                  </a>
                ))}
              </div>
            </nav>
          )}
          <article
            key="prose"
            ref={prose}
            className="se-prose"
            data-cfg="infosBody"
            dangerouslySetInnerHTML={content}
          />
        </div>
      )}
    </section>
  );
}
